import * as Dialog from '@radix-ui/react-dialog'
import { CheckCircle2, CircleDot, Clock3, X } from 'lucide-react'
import { cn } from '@merels/ui'
import type { DevelopmentProgress } from '../../../deps/catalog/developmentProgress.types'

export type { DevelopmentProgress } from '../../../deps/catalog/developmentProgress.types'

const maturityPresentation = {
  draft: { label: 'Draft', description: 'The agent is working toward the first playable version.', classes: 'border-sky-800/60 bg-sky-950/90 text-sky-300' },
  alpha: { label: 'Alpha', description: 'Playable for testing with friends; hardening and accessibility polish still remain.', classes: 'border-amber-800/60 bg-amber-950/90 text-amber-300' },
  beta: { label: 'Beta', description: 'Hardened and audited; ready for regular play with friends.', classes: 'border-violet-800/60 bg-violet-950/90 text-violet-300' },
  release: { label: 'Released', description: 'Published in the Merels catalog for the wider audience.', classes: 'border-emerald-800/60 bg-emerald-950/90 text-emerald-300' },
} as const

/** Renders the explanatory lifecycle content shared by cards and dialogs. */
export function DevelopmentStatusDetails({ progress }: { progress: DevelopmentProgress }) {
  const presentation = maturityPresentation[progress.maturity]
  const Icon = progress.state === 'complete' ? CheckCircle2 : progress.state === 'awaiting_creator' ? Clock3 : CircleDot

  return (
    <div className="text-left">
      <p className="text-sm leading-relaxed text-muted-foreground">{presentation.description}</p>

      {progress.currentStage && (
        <div className="mt-4 flex gap-2 text-sm text-gray-200">
          <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div>
            <p className="font-semibold">
              {progress.state === 'awaiting_creator' ? 'Waiting for you: ' : 'Current step: '}
              {progress.currentStage.title}
            </p>
            <p className="mt-1 text-muted-foreground">{progress.currentStage.summary}</p>
          </div>
        </div>
      )}

      {progress.maturity === 'alpha' && progress.remainingToBeta.length > 0 && (
        <div className="mt-4 border-t border-[#272b3f] pt-4 text-sm text-gray-300">
          <p className="font-semibold text-gray-200">
            {progress.remainingToBeta.length} steps remaining to Beta
          </p>
          <ol className="mt-2 list-decimal space-y-1 pl-5 text-muted-foreground">
            {progress.remainingToBeta.map((step) => <li key={step}>{step}</li>)}
          </ol>
        </div>
      )}
    </div>
  )
}

/** Presents durable game maturity while the creator is still in the new-game flow. */
export function DevelopmentStatusCard({ progress }: { progress: DevelopmentProgress }) {
  const presentation = maturityPresentation[progress.maturity]

  return (
    <article className="rounded-lg border border-[#272b3f] bg-[#10121a] p-4 text-left" aria-label={`${progress.displayName} development status`}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-gray-100">{progress.displayName}</h3>
          <p className="truncate text-[10px] text-muted-foreground">{progress.gameId}</p>
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-1 text-[9px] font-bold uppercase tracking-wider ${presentation.classes}`}>
          {presentation.label}
        </span>
      </div>
      <DevelopmentStatusDetails progress={progress} />
    </article>
  )
}

/** Opens creator-only lifecycle details from a compact badge. */
export function DevelopmentStatusBadge({
  progress,
  className,
  onClick,
}: {
  progress: DevelopmentProgress
  className?: string
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void
}) {
  const presentation = maturityPresentation[progress.maturity]

  if (onClick) {
    return (
      <button
        type="button"
        className={cn(
          'rounded-full border px-2 py-1 text-[9px] font-bold uppercase tracking-wider shadow-sm transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
          presentation.classes,
          className,
        )}
        aria-label={`${presentation.label} development status for ${progress.displayName}`}
        onClick={(event) => {
          event.stopPropagation()
          onClick(event)
        }}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {presentation.label}
      </button>
    )
  }

  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>
        <button
          type="button"
          className={cn(
            'rounded-full border px-2 py-1 text-[9px] font-bold uppercase tracking-wider shadow-sm transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
            presentation.classes,
            className,
          )}
          aria-label={`${presentation.label} development status for ${progress.displayName}`}
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          {presentation.label}
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[10000] bg-overlay" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-[10001] w-[min(90vw,30rem)] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-basic bg-background p-6 shadow-xl focus:outline-none"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <Dialog.Title className="truncate text-lg font-bold text-primary">
                {progress.displayName}
              </Dialog.Title>
              <Dialog.Description className="mt-1 text-xs text-muted">
                {presentation.label} · {progress.gameId}
              </Dialog.Description>
            </div>
            <Dialog.Close
              className="rounded p-1 text-muted transition-colors hover:bg-muted-bg hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              aria-label="Close development status"
            >
              <X className="h-4 w-4" />
            </Dialog.Close>
          </div>
          <div className="mt-5">
            <DevelopmentStatusDetails progress={progress} />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
